import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePutIngredientDataToQueue } from "../../../../api/ingredients";
import { IngredientDataType } from "../../../../config/enums/IngredientType.enum";
import { MappingSourceType } from "../../../../config/enums/MappingSourceType.enum";
import { IngredientRowViewModel } from "../../ingredientList/types/ingredientListView.types";
import { IngredientDetailsViewModel } from "../types/ingredientDetailsView.types";

interface UseIngredientDetailsViewModelParams {
  ingredientDetails: IngredientRowViewModel | undefined;
  setVisibleModal: (visible: boolean) => void;
  navigateIngredientEdit: (visible: boolean) => void;
}

/**
 * Owns the details modal's own state (tab selection, calculate flow) —
 * counterpart to `useIngredientRegisterViewModel`/`useIngredientEditViewModel`.
 * Each tab component (`components/tabs/*`) fetches its own data directly,
 * matching the legacy `IngredientViewTabs/*` split — this hook only owns what
 * the modal chrome (header/tabs/footer) itself needs.
 */
export const useIngredientDetailsViewModel = ({
  ingredientDetails,
  setVisibleModal,
  navigateIngredientEdit
}: UseIngredientDetailsViewModelParams): IngredientDetailsViewModel => {
  const navigation = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [calculateModalOpen, setCalculateModalOpen] = useState(false);

  const { mutate: putIngredientDataToQueue, isLoading: isLoadingPutDataToQueue } =
    usePutIngredientDataToQueue();

  const ingredientId = ingredientDetails?.ingredient_id;
  const supplierId = ingredientDetails?.supplier_id || undefined;
  const currentDataType = ingredientDetails?.supplierIngredientDetails?.data_type;

  const enableIngredientMapping = useMemo(
    () =>
      Boolean(
        (currentDataType === IngredientDataType.USER ||
          currentDataType === IngredientDataType.GENERIC) &&
          ingredientId &&
          supplierId
      ),
    [currentDataType, ingredientId, supplierId]
  );

  const ingredientButtonTitle =
    currentDataType === IngredientDataType.USER ? "Ingredient Mapping" : "Calculate";

  const navigateIngredientMapping = useCallback(() => {
    navigation(
      `/mapping/${MappingSourceType.ingredient}?sourceId=${ingredientId}&ownerId=${supplierId}&ingredientName=${ingredientDetails?.ingredient_name}`
    );
  }, [navigation, ingredientId, supplierId, ingredientDetails?.ingredient_name]);

  const onIngredientButtonClick = useCallback(() => {
    if (currentDataType === IngredientDataType.USER) {
      navigateIngredientMapping();
    } else {
      setCalculateModalOpen(true);
    }
  }, [currentDataType, navigateIngredientMapping]);

  const onApproveCalculate = useCallback(() => {
    putIngredientDataToQueue(
      { ingredientId: ingredientId as string, supplierId: supplierId as string },
      {
        onSuccess: () => {
          setCalculateModalOpen(false);
          setSelectedTab(1);
        },
        onError: () => setCalculateModalOpen(false)
      }
    );
  }, [putIngredientDataToQueue, ingredientId, supplierId]);

  return {
    ingredientDetails,
    selectedTab,
    setSelectedTab,
    ingredientId,
    supplierId,
    currentDataType,
    enableIngredientMapping,
    ingredientButtonTitle,
    isLoadingPutDataToQueue,
    calculateModalOpen,
    onOpenCalculateModal: () => setCalculateModalOpen(true),
    onCloseCalculateModal: () => setCalculateModalOpen(false),
    onApproveCalculate,
    onCancel: () => setVisibleModal(false),
    onEdit: () => navigateIngredientEdit(true),
    onIngredientButtonClick
  };
};
